import { useEffect, useRef, useCallback } from "react";

// Avoid touching `document` at module scope (SSR / non-browser safety).
const getSafeDocument = (): Document | null =>
  typeof document !== "undefined" ? document : null;

// Shared state across all hook instances to prevent race conditions
// Using reference counting to handle multiple components blocking scroll
let scrollBlockCount = 0;
let originalStyles: {
  html: { position: string; overflow: string };
  body: { position: string; overflow: string; paddingRight: string };
} | null = null;

/**
 * Test-only helper to reset module-level state.
 *
 * Cypress component tests run in a single browser session and can be affected
 * by previous specs. This provides a safe, explicit way to reset the shared
 * reference-counting state.
 */
export const __resetScrollBlockForTests = (): void => {
  scrollBlockCount = 0;

  const doc = getSafeDocument();
  if (!doc) {
    originalStyles = null;
    return;
  }

  const html = doc.documentElement;
  const { body } = doc;

  if (html) {
    html.style.position = "";
    html.style.overflow = "";
  }

  if (body?.style) {
    body.style.position = "";
    body.style.overflow = "";
    body.style.paddingRight = "";
  }

  originalStyles = null;
};

/**
 * Internal function to block scroll (shared across all instances)
 */
const blockScrollInternal = (): void => {
  const doc = getSafeDocument();
  if (!doc || typeof window === "undefined") return;

  const html = doc.documentElement;
  const { body } = doc;

  if (!body || !body.style || !html) return;

  // Increment reference count atomically
  scrollBlockCount++;

  // If already blocked, just increment the counter
  if (scrollBlockCount > 1) {
    return;
  }

  const scrollBarWidth = window.innerWidth - html.clientWidth;
  const bodyPaddingRight =
    parseInt(
      window.getComputedStyle(body).getPropertyValue("padding-right"),
      10,
    ) || 0;

  // Store original styles only on first block
  originalStyles = {
    html: {
      position: html.style.position || "",
      overflow: html.style.overflow || "",
    },
    body: {
      position: body.style.position || "",
      overflow: body.style.overflow || "",
      paddingRight: body.style.paddingRight || "",
    },
  };

  /**
   * 1. Fixes a bug in iOS and desktop Safari whereby setting
   *    `overflow: hidden` on the html/body does not prevent scrolling.
   * 2. Fixes a bug in desktop Safari where `overflowY` does not prevent
   *    scroll if an `overflow-x` style is also applied to the body.
   */
  html.style.position = "relative"; /* [1] */
  html.style.overflow = "hidden"; /* [2] */
  // body.style.position = "relative"; /* [1] */
  body.style.overflow = "hidden"; /* [2] */
  body.style.paddingRight = `${bodyPaddingRight + scrollBarWidth}px`;
};

/**
 * Internal function to allow scroll (shared across all instances)
 */
const allowScrollInternal = (): void => {
  const doc = getSafeDocument();
  if (!doc) return;

  const html = doc.documentElement;
  const { body } = doc;

  if (!body || !body.style || !html) return;

  // Decrement reference count atomically.
  scrollBlockCount = Math.max(0, scrollBlockCount - 1);

  // Only actually unblock if count reaches zero
  if (scrollBlockCount > 0) {
    return;
  }

  // Restore original styles when count reaches zero.
  // Even if originalStyles was lost, always clear the styles we set.
  html.style.position = originalStyles?.html.position ?? "";
  html.style.overflow = originalStyles?.html.overflow ?? "";
  body.style.position = originalStyles?.body.position ?? "";
  body.style.overflow = originalStyles?.body.overflow ?? "";
  body.style.paddingRight = originalStyles?.body.paddingRight ?? "";

  originalStyles = null;
};

/**
 * Usage:
 * const [blockScroll, allowScroll] = useScrollBlock();
 */
export const useScrollBlock = (): [() => void, () => void] => {
  const isBlockedByThisInstance = useRef(false);

  // Cleanup on unmount: if this instance blocked scroll, unblock it
  useEffect(() => {
    return () => {
      if (isBlockedByThisInstance.current) {
        allowScrollInternal();
        isBlockedByThisInstance.current = false;
      }
    };
  }, []);

  const blockScroll = useCallback((): void => {
    if (isBlockedByThisInstance.current) return;

    blockScrollInternal();
    isBlockedByThisInstance.current = true;
  }, []);

  const allowScroll = useCallback((): void => {
    if (isBlockedByThisInstance.current) {
      allowScrollInternal();
      isBlockedByThisInstance.current = false;
    }
  }, []);

  return [blockScroll, allowScroll];
};
